import React from 'react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-24 bg-[#f7f5f2]">
      <SEO title="Privacy Policy" description="Speaking Mirror Privacy Policy — how we collect, use, store, and protect your information." />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold text-[#1e1919] mb-2">Privacy Policy</h1>
        <p className="text-[#736c64] text-sm mb-10">© 2026 Speaking Mirror. All Rights Reserved.</p>

        <div className="prose prose-lg max-w-none text-[#1e1919] space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">1. Introduction</h2>
            <p className="text-[#736c64] leading-relaxed">Welcome to Speaking Mirror. We are committed to protecting your privacy and ensuring that your personal information is handled securely and responsibly.</p>
            <p className="text-[#736c64] leading-relaxed">This Privacy Policy explains how Speaking Mirror collects, uses, stores, and protects information when you visit our website, submit inquiries, engage with our services, or interact with us online.</p>
            <p className="text-[#736c64] leading-relaxed">By using our website, you consent to the practices described in this Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">2. About Us</h2>
            <p className="text-[#736c64] leading-relaxed">Speaking Mirror is a full-scale digital marketing and content creation agency based in Kolkata, India, offering services including digital marketing, social media management, content creation, branding, website development, SEO, advertising, influencer marketing, video production, and related marketing solutions.</p>
            <div className="text-[#736c64] leading-relaxed mt-3">
              <p><strong className="text-[#1e1919]">Business Address:</strong><br />Jasmine Towers, Suites 201 & 202, 31, Shakespeare Sarani, Kolkata, West Bengal 700017, India</p>
              <p><strong className="text-[#1e1919]">Email:</strong> team@speakingmirror.in</p>
              <p><strong className="text-[#1e1919]">Phone:</strong> +91 9830471770</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">3. Information We Collect</h2>
            <p className="text-[#736c64] leading-relaxed mb-2">We may collect the following information:</p>
            <h3 className="text-lg font-bold text-[#1e1919] mt-4 mb-2">Personal Information</h3>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>Full Name</li><li>Email Address</li><li>Phone Number</li><li>Company Name</li><li>Business Information</li><li>Any information voluntarily submitted through forms, emails, WhatsApp, or other communication channels</li>
            </ul>
            <h3 className="text-lg font-bold text-[#1e1919] mt-4 mb-2">Technical Information</h3>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>IP Address</li><li>Browser Type</li><li>Device Information</li><li>Operating System</li><li>Pages Visited</li><li>Website Usage Data</li><li>Referral Sources</li><li>Cookies and Similar Technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">4. How We Use Your Information</h2>
            <p className="text-[#736c64] leading-relaxed mb-2">We may use your information to:</p>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>Respond to inquiries and requests</li><li>Provide our services</li><li>Communicate regarding projects and campaigns</li><li>Improve website performance and user experience</li><li>Send marketing or promotional communications (where permitted)</li><li>Analyze website traffic and visitor behavior</li><li>Maintain security and prevent fraudulent activity</li><li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">5. Cookies and Tracking Technologies</h2>
            <p className="text-[#736c64] leading-relaxed">Our website may use cookies, analytics tools, and similar technologies to improve user experience and measure website performance.</p>
            <p className="text-[#736c64] leading-relaxed mt-2">These technologies may collect information regarding website visits, user interactions, device information, and traffic sources.</p>
            <p className="text-[#736c64] leading-relaxed mt-2">You may disable cookies through your browser settings; however, certain website features may not function properly.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">6. Third-Party Services</h2>
            <p className="text-[#736c64] leading-relaxed mb-2">We may use trusted third-party services including:</p>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>Google Analytics</li><li>Google Ads</li><li>Meta (Facebook & Instagram) Advertising Tools</li><li>Website Hosting Providers</li><li>CRM and Marketing Platforms</li><li>Email Communication Services</li>
            </ul>
            <p className="text-[#736c64] leading-relaxed mt-2">These third-party providers may collect and process information according to their own privacy policies.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">7. Data Sharing</h2>
            <p className="text-[#736c64] leading-relaxed">Speaking Mirror does not sell, rent, or trade your personal information.</p>
            <p className="text-[#736c64] leading-relaxed mt-2 mb-2">We may share information only:</p>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>With trusted service providers assisting in business operations</li><li>When required by law or government authorities</li><li>To protect our legal rights and security</li><li>During business restructuring, merger, or acquisition (if applicable)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">8. Data Security</h2>
            <p className="text-[#736c64] leading-relaxed">We implement reasonable technical and organizational measures to protect personal information from unauthorized access, misuse, alteration, disclosure, or destruction.</p>
            <p className="text-[#736c64] leading-relaxed mt-2">While we strive to protect your information, no online transmission or storage system can be guaranteed as completely secure.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">9. Data Retention</h2>
            <p className="text-[#736c64] leading-relaxed mb-2">We retain personal information only for as long as necessary to:</p>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>Deliver services</li><li>Fulfill contractual obligations</li><li>Resolve disputes</li><li>Maintain business records</li><li>Comply with legal requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">10. Your Rights</h2>
            <p className="text-[#736c64] leading-relaxed mb-2">Subject to applicable laws, you may have the right to:</p>
            <ul className="list-disc pl-6 text-[#736c64] space-y-1">
              <li>Request access to your personal information</li><li>Request correction of inaccurate information</li><li>Request deletion of personal information</li><li>Withdraw consent where applicable</li><li>Object to certain processing activities</li>
            </ul>
            <p className="text-[#736c64] leading-relaxed mt-2">To exercise these rights, please contact us using the details provided below.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">11. External Links</h2>
            <p className="text-[#736c64] leading-relaxed">Our website may contain links to third-party websites. We are not responsible for the privacy practices, content, or security of external websites. Users are encouraged to review the privacy policies of those websites separately.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">12. Children's Privacy</h2>
            <p className="text-[#736c64] leading-relaxed">Our services are not intentionally directed toward children under the age of 13. We do not knowingly collect personal information from children. If such information is identified, appropriate steps will be taken to remove it.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">13. Changes to This Privacy Policy</h2>
            <p className="text-[#736c64] leading-relaxed">Speaking Mirror reserves the right to update or modify this Privacy Policy at any time. Any updates will be posted on this page along with the revised effective date. Continued use of the website after changes constitutes acceptance of the updated Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#1e1919] mb-3">14. Contact Us</h2>
            <p className="text-[#736c64] leading-relaxed">If you have any questions regarding this Privacy Policy or the handling of your information, please contact:</p>
            <div className="text-[#736c64] leading-relaxed mt-3">
              <p><strong className="text-[#1e1919]">Speaking Mirror</strong></p>
              <p>Jasmine Towers, Suites 201 & 202, 31, Shakespeare Sarani, Kolkata, West Bengal 700017, India</p>
              <p>Email: team@speakingmirror.in</p>
              <p>Phone: +91 9830471770</p>
              <p>Website: https://www.speakingmirror.in</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
